import {
  Assignment,
  CommuteOutlined,
  Redeem,
  ScaleOutlined,
  WorkOutlineTwoTone,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import HandymanIcon from "@mui/icons-material/Handyman";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Box, Stack, Typography } from "@mui/material";

import { CarrierShipmentDetailFiles } from "components/Carrier/components/CarrierShipmentDetailFiles";
import { CustomerInformationSection } from "components/Shipper/components/ShipmentOverviewExpanded/components/CustomerInformationSection";
import { ShipperShipmentDetailFiles } from "components/Shipper/components/ShipperShipmentDetailFiles";
import { useRoleContext } from "hooks";
import {
  capitalizeFirst,
  formatText,
  formatWeightLb,
  getCommdityNameForCondensedShipmentShipment,
  getCurrencyName,
  getEquipmentTypesDisplay as getEquipmentTypesAsSingleString,
  getServiceTypeDisplayLabel,
  ISOStringToLocalReadableTimestamp,
} from "utils";
import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { ShipmentDetail } from "../ShipmentDetail";

interface ShipmentDetailLikeShipment {
  info: {
    weight_lb: number | null;
    service: string;
    additional_details: string | null;
    special_requirements: string | null;
    type: ShipmentType;
    commodity?: Commodity | null;
    commodity_freetext: string | null;
    quoting_unit_of_measure: UnitOfMeasureType;
    created_at?: string;
  };
  equipments: Equipment[];
  book_now_info?: {
    is_book_now: boolean;
    book_now_price: number | null;
    book_now_currency: string | null;
  };
  project?: {
    loads_available: number;
    loads_booked: number;
  } | null;
}

interface Props {
  shipment: ShipmentDetailLikeShipment | undefined;
}
export function ShipmentDetails(props: Props) {
  const { shipment } = props;
  const { role } = useRoleContext();

  const shipmentInfo = shipment?.info;
  const equipmentTypes = shipment?.equipments;

  const getEquipmentTypesDisplay = () => {
    if (!equipmentTypes) {
      return "";
    }
    const equipmentTypeNames = equipmentTypes.map((eq) => eq.name);
    return getEquipmentTypesAsSingleString(equipmentTypeNames);
  };
  return (
    <Stack direction="column" spacing={2}>
      <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
        {role?.type === "CARRIER" && shipment?.book_now_info?.is_book_now && (
          <ShipmentDetail
            icon={<AttachMoneyIcon fontSize="small" />}
            title="Target Price"
            text={
              shipment?.book_now_info?.book_now_price &&
              shipment?.book_now_info?.book_now_price !== 0 &&
              shipment?.book_now_info?.book_now_currency
                ? `$${shipment?.book_now_info?.book_now_price}
          ${getCurrencyName(shipment?.book_now_info.book_now_currency)}`
                : "-"
            }
          />
        )}
        <ShipmentDetail
          icon={<Redeem />}
          title="Commodity"
          text={
            shipment?.info
              ? capitalizeFirst(
                  formatText(
                    getCommdityNameForCondensedShipmentShipment(shipment.info),
                  ),
                )
              : "-"
          }
        />
        <ShipmentDetail
          icon={<ScaleOutlined fontSize="small" />}
          title="Weight"
          text={
            shipmentInfo?.weight_lb != null
              ? formatWeightLb(shipmentInfo.weight_lb)
              : "-"
          }
        />
        <ShipmentDetail
          icon={<WorkOutlineTwoTone fontSize="small" />}
          title="Service"
          text={getServiceTypeDisplayLabel(shipmentInfo?.service ?? "-")}
        />
        <ShipmentDetail
          icon={<CommuteOutlined fontSize="small" />}
          title="Equipment Type(s)"
          text={
            equipmentTypes && equipmentTypes.length > 0
              ? getEquipmentTypesDisplay()
              : "-"
          }
        />
        <ShipmentDetail
          icon={<Assignment fontSize="small" />}
          title="Product Dimensions"
          multiline
          text={shipment?.info?.additional_details ?? "-"}
        />
        <ShipmentDetail
          icon={<HandymanIcon fontSize="small" />}
          title="Special Requirements"
          multiline
          text={shipment?.info?.special_requirements ?? "-"}
        />
        <ShipmentDetail
          icon={<LocalOfferIcon fontSize="small" />}
          title="Pricing Mode"
          multiline
          text={
            shipment?.info?.quoting_unit_of_measure
              ? formatQuotingUnitOfMeasure(
                  shipment.info.quoting_unit_of_measure,
                )
              : "-"
          }
        />
        {role?.type === "SHIPPER" && shipment?.book_now_info?.is_book_now && (
          <ShipmentDetail
            icon={<AttachMoneyIcon fontSize="small" />}
            title="Book Now Price"
            text={
              shipment?.book_now_info?.book_now_price &&
              shipment?.book_now_info?.book_now_price !== 0 &&
              shipment?.book_now_info?.book_now_currency
                ? `$${shipment?.book_now_info?.book_now_price}
          ${getCurrencyName(shipment?.book_now_info.book_now_currency)}`
                : "-"
            }
          />
        )}
        {shipment?.info?.type === "MULTI_SHIPMENT_PROJECT" &&
          shipment?.project && (
            <ShipmentDetail
              icon={<LocalShippingIcon />}
              title="Available Shipments"
              text={shipment?.project?.loads_available}
            />
          )}
        {shipment?.info?.type === "MULTI_SHIPMENT_PROJECT" &&
          shipment?.project && (
            <ShipmentDetail
              icon={<CheckBoxIcon />}
              title="Shipments Booked"
              text={shipment?.project?.loads_booked}
            />
          )}
        <ShipmentDetail
          icon={<AccessTimeIcon fontSize="small" />}
          title="Posted at"
          text={
            shipment?.info?.created_at
              ? ISOStringToLocalReadableTimestamp(shipment.info.created_at)
              : "-"
          }
        />
      </Box>
      {shipment?.info?.type === "QUOTES_ONLY" && (
        <Typography variant="h6">
          **This shipment is for quoting only**
        </Typography>
      )}
      <CustomerInformationSection
        shipment={shipment as ShipperCondensedShipment}
      />
      {role?.type === "SHIPPER" && <ShipperShipmentDetailFiles />}
      {role?.type === "CARRIER" && <CarrierShipmentDetailFiles />}
    </Stack>
  );
}
